import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  DeleteTaskGroupTemplateDocument,
  type DeleteTaskGroupTemplateMutation,
  type DeleteTaskGroupTemplateMutationVariables,
  type SimpleTaskFragment,
  type TaskGroupTemplateFragment,
  TaskGroupTemplatesDocument,
  type TaskGroupTemplatesQuery,
  type TaskGroupTemplatesQueryVariables,
  UpdateTaskGroupTemplateDocument,
  type UpdateTaskGroupTemplateMutation,
  type UpdateTaskGroupTemplateMutationVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { compact } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import type { TaskGroupTemplateCreateModalStore } from "./modal/store";

type Task = NonNullable<TaskGroupTemplateFragment["tasks"]>[0];
export class TaskGroupTemplatesStore {
  fetcher = new FetchHelper<
    TaskGroupTemplatesQuery,
    TaskGroupTemplatesQueryVariables
  >(TaskGroupTemplatesDocument);
  updateMutator = new MutationHelper<
    UpdateTaskGroupTemplateMutation,
    UpdateTaskGroupTemplateMutationVariables
  >(UpdateTaskGroupTemplateDocument);
  deleteMutator = new MutationHelper<
    DeleteTaskGroupTemplateMutation,
    DeleteTaskGroupTemplateMutationVariables
  >(DeleteTaskGroupTemplateDocument);

  @observable
  taskGroupTemplates: TaskGroupTemplateFragment[] = [];
  @observable collapsedTaskGroups: Set<string> = new Set();
  @observable.ref groupTemplateIdToDelete: string | null = null;

  constructor(
    private appStore: AppStore,
    public modalStore: TaskGroupTemplateCreateModalStore,
  ) {
    makeObservable(this);
    this.fetchTableData();
  }

  @computed get isDeleteModalOpen() {
    return Boolean(this.groupTemplateIdToDelete);
  }

  @action.bound async fetchTableData() {
    const [data, error] = await this.fetcher.fetch();

    if (error) return;

    this.taskGroupTemplates = compact(data.taskTemplateGroups);
  }

  @action.bound handleCreateTemplate() {
    this.modalStore.disclosure.onOpen({
      onSubmit: (template) => {
        this.taskGroupTemplates.push(template);
      },
    });
  }

  @action.bound async updateTemplateGroup(
    id: TaskGroupTemplateFragment["id"],
    name: string,
  ) {
    const [data, error] = await this.updateMutator.mutate({
      id,
      name,
    });

    if (error) {
      this.appStore.UIStore.toast({
        status: "error",
      });
      return;
    }

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Task group updated successfully.`,
    });

    const templatesCount = this.taskGroupTemplates.length;
    for (let i = 0; i < templatesCount; i++) {
      if (this.taskGroupTemplates[i].id !== data.updateTaskTemplateGroup.id)
        continue;
      this.taskGroupTemplates[i] = data.updateTaskTemplateGroup;
    }
  }

  @action.bound async deleteTemplateGroup() {
    if (!this.groupTemplateIdToDelete) {
      captureEvent({
        message: "FE: Missing task group template ID for delete modal.",
      });
      this.appStore.UIStore.toast({
        status: "error",
      });
      return;
    }
    const [data, error] = await this.deleteMutator.mutate({
      id: this.groupTemplateIdToDelete,
    });

    if (error) {
      return;
    }

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Task group deleted successfully.`,
    });

    if (!data.deleteTaskTemplateGroup) return;
    const indexToDelete = this.taskGroupTemplates.findIndex(
      (template) => template.id === this.groupTemplateIdToDelete,
    );
    if (indexToDelete === -1) return;
    this.taskGroupTemplates.splice(indexToDelete, 1);
  }

  @action.bound handleAddTask(templateId: string) {
    this.appStore.taskFormModalStore.modalState.onOpen({
      isTemplate: true,
      templateGroupId: templateId,
      filterParentTasksForGroup: true,
      onSubmit: (task) => {
        for (const template of this.taskGroupTemplates) {
          if (template.id !== templateId) continue;
          template.tasks?.push(task);
        }
      },
    });
  }

  @action.bound handleTaskClick(taskId: string) {
    this.appStore.taskDetailModalStore.disclosureState.onOpen({
      id: taskId,
      onChange: this.fetchTableData,
      onDelete: this.fetchTableData,
      onDuplicate: this.fetchTableData,
    });
  }

  @action.bound handleOnCreateSubtask(task: Task) {
    for (const template of this.taskGroupTemplates) {
      if (template.id !== task.task_template_group_id) continue;
      if (!template?.tasks) continue;

      for (const templateTask of template.tasks) {
        if (templateTask.id !== task.parent?.id) continue;
        if (Array.isArray(templateTask.subtasks)) {
          templateTask.subtasks?.push(task as SimpleTaskFragment);
        } else {
          templateTask.subtasks = [task as SimpleTaskFragment];
        }
      }
    }
  }

  @action.bound handleCollapseGroup(templateGroupId: string) {
    if (this.collapsedTaskGroups.has(templateGroupId)) {
      this.collapsedTaskGroups.delete(templateGroupId);
    } else {
      this.collapsedTaskGroups.add(templateGroupId);
    }
  }

  @action.bound handleSetGroupToDelete(templateGroupId: string | null) {
    this.groupTemplateIdToDelete = templateGroupId;
  }
}
